import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../config/firebase';
import { useConnectionStatus } from '../hooks/useConnectionStatus';
import NoConnectionPage from '../pages/NoConnectionPage';
import { useLanguage } from '../contexts/LanguageContext';
import { EngineeringLoadingScreen } from './EngineeringLoadingScreen';

interface ProtectedRouteProps {
    children: React.ReactNode;
    // If true, route requires authentication (default: true)
    requireAuth?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
    children, 
    requireAuth = true 
}) => {
    const [user, setUser] = useState<User | null>(null);
    const [isAuthChecking, setIsAuthChecking] = useState(true);
    const isOnline = useConnectionStatus();
    const location = useLocation();
    const { strings } = useLanguage();

    // Subscribe to Firebase Auth state changes
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            setUser(firebaseUser);
            setIsAuthChecking(false);
        });

        return () => unsubscribe();
    }, []);

    // Show loading state while checking authentication
    if (isAuthChecking) {
        return <EngineeringLoadingScreen ariaLabel={strings.LOADING} />;
    }

    // Check connection status - show offline page if no connection
    if (!isOnline) {
        return <NoConnectionPage />;
    }

    // If authentication is required but user is not authenticated, redirect to login
    if (requireAuth && !user) {
        // Save the attempted URL for redirecting after login
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // If user is authenticated and trying to access login/register, redirect to home
    if (!requireAuth && user) {
        // Redirect authenticated users away from public pages
        const redirectTo = location.state?.from?.pathname || '/home';
        return <Navigate to={redirectTo} replace />;
    }

    // Render the protected content
    return <>{children}</>;
};

export default ProtectedRoute;














